import {
  AdminLayout,
  useLocalStorage,
  useMemoAsync,
  useSource,
  useTranslation,
} from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { AdminToolbar, CalendarWithoutModeSelect } from "@opendash/ui";
import { Button, CalendarProps, Checkbox } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useState } from "react";
import styled from "styled-components";
import {
  ScheduleTemplateDetail,
  ScheduleWithBadge,
  useScheduleForSingleDate,
  useSourceWithChildren,
} from "../../features/schedules";
import SpontaneousScheduleModal from "../../features/schedules/components/SpontaneousScheduleModal";

const StyledEvents = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  .ant-badge-status {
    width: 100%;
    overflow: hidden;
    font-size: 12px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

export const ScheduleCalendarPage = observer(() => {
  const t = useTranslation();
  const [source] = useSource();
  const validIds = useSourceWithChildren(source?.id);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
  const { scheduleStates, getSchedulesOnDate, refetchSchedulesOnDate } =
    useScheduleForSingleDate();
  const [spontaneusScheduleModalOpen, setSpontaneusScheduleModalOpen] =
    useState<boolean>(false);
  const [includeChildren, setIncludeChildren] = useLocalStorage(
    "openservice:show-children-in-schedule-table",
    false
  );

  const sourceObj = useMemoAsync(
    () => (source.id ? new Parse.Query(Source).get(source?.id) : undefined),
    [source?.id]
  );

  const cellRender: CalendarProps<Dayjs>["cellRender"] = (current, info) => {
    if (info.type === "date") {
      return (
        <StyledEvents className="events">
          {getSchedulesOnDate(current)
            .filter((s) =>
              includeChildren
                ? validIds.some((v) => s.sources?.some((src) => src.id === v))
                : s.sources?.some((src) => src.id === source.id)
            )
            .map((scheduleState, index) => (
              <li key={scheduleState.schedule.id + index}>
                <ScheduleWithBadge schedule={scheduleState.schedule} />
              </li>
            ))}
        </StyledEvents>
      );
    }
    return info.originNode;
  };

  return (
    <>
      <AdminLayout contentPadding>
        <AdminToolbar
          title={t("maintenance:schedule.calendar.title", {
            name: source.name,
          })}
          actions={[
            <Checkbox
              key="include-children"
              checked={includeChildren}
              onChange={(e) => setIncludeChildren(e.target.checked)}
            >
              {t("maintenance:schedule.source.actions.filter.children")}
            </Checkbox>,
            <Button
              key="spontaneous"
              onClick={() => setSpontaneusScheduleModalOpen(true)}
              type="primary"
            >
              {t("maintenance:machinelog.create.schedule")}
            </Button>,
          ]}
        />
        <CalendarWithoutModeSelect
          onSelect={(value: Dayjs, info) => {
            if (info.source === "date") {
              setSelectedDate(value);
              setOpenDetailModal(true);
            }
          }}
          cellRender={cellRender}
        />
      </AdminLayout>

      <ScheduleTemplateDetail
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        date={selectedDate}
        onSave={() => refetchSchedulesOnDate()}
      />

      {sourceObj && (
        <SpontaneousScheduleModal
          open={spontaneusScheduleModalOpen}
          sources={[sourceObj]}
          onClose={() => setSpontaneusScheduleModalOpen(false)}
          onSaveSuccess={() => {
            setSpontaneusScheduleModalOpen(false);
            refetchSchedulesOnDate();
          }}
        />
      )}
    </>
  );
});
